import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  FlexBox,
  Row,
  Column,
  Card,
  ListItem,
  List,
  H4,
} from '@vp/swan';
import { useTranslations, ReplaceTokens } from '@vp/om-translations';
import { useDispatch, useSelector } from 'react-redux';
import { ModTarget } from '@vp/mod-father';
import { CartItem } from './CartItem';
import { ItemGroupPricingSummary } from './ItemGroupPricingSummary';
import { ItemModWrapper } from '../uxMods/ItemModWrapper';
import { ConfirmationModal } from '../ConfirmationModal';
import { cartItemOperations, cartItemSelectors } from '../../state/cartItems';
import useSiteData from '../../hooks/useSiteData';
import { TaxDisplayContext } from '../shared/taxDisplay/TaxDisplayContext';

export const CartItemGroup = ({ cartItemIds, groupId }) => {
  const [cartMessages] = useTranslations(['cartMessages']);
  const { locale } = useSiteData();
  const dispatch = useDispatch();
  const removeItemGroup = () =>
    dispatch(cartItemOperations.removeItemGroup(cartItemIds, groupId));

  const suppressItemGroupDisplay = useSelector(state =>
    cartItemSelectors.selectSuppressDefaultGroupDisplay(
      state.cartItems,
      groupId
    )
  );
  const itemGroupTitle = useSelector(state =>
    cartItemSelectors.selectItemGroupTitle(state.cartItems, groupId, locale)
  );
  const productNames = useSelector(state =>
    cartItemSelectors.selectProductNamesForItemIds(state.cartItems, cartItemIds)
  );

  const blockUserInteraction = useSelector(state =>
    cartItemSelectors.selectAreAnySpecificItemsBlockingUserInteraction(
      state.cartItems,
      cartItemIds
    )
  );

  const { clusivitySeparatedItemGroupPrices } = useContext(TaxDisplayContext);
  const displayItemGroupPricingSummary =
    clusivitySeparatedItemGroupPrices &&
    clusivitySeparatedItemGroupPrices[groupId] &&
    clusivitySeparatedItemGroupPrices[groupId].length > 0;

  const joinedProductNames = productNames.join(', ');
  const removeItemVisuallyHiddenLabel = (
    <ReplaceTokens
      tokenizedString={cartMessages.removeProductFromCart}
      substitutions={{ productName: itemGroupTitle || joinedProductNames }}
    />
  );

  return (
    <Row>
      <Column span={12}>
        <Card bordered>
          <Row>
            <Column span={9}>
              {itemGroupTitle ? (
                <FlexBox justifyContent="flex-start" flexWrap="wrap">
                  <H4
                    fontSize="standard"
                    fontWeight="bold"
                    id={`product-name-${groupId}`}
                  >
                    {itemGroupTitle}
                  </H4>
                </FlexBox>
              ) : (
                <H4 visuallyHidden id={`product-name-${groupId}`}>
                  {joinedProductNames}
                </H4>
              )}
            </Column>
            <Column span={3}>
              <FlexBox justifyContent="flex-end" flexWrap="wrap">
                <ConfirmationModal
                  linkText={cartMessages.remove}
                  linkDisabled={blockUserInteraction}
                  prompt={cartMessages.removeItemWarning}
                  cancelText={cartMessages.removeItemDecline}
                  confirmText={cartMessages.removeItemConfirm}
                  action={removeItemGroup}
                  visuallyHiddenModalDialogLabel={removeItemVisuallyHiddenLabel}
                  linkTextDescribedBy={`product-name-${groupId}`}
                />
              </FlexBox>
            </Column>
          </Row>
          {suppressItemGroupDisplay ? (
            <>
              <ModTarget name="entireItemGroup" />
              {/* render item-level mod bosses to enable item-level mods that target page-level ModTargets */}
              {cartItemIds.map(cartItemId => (
                <ItemModWrapper
                  itemId={cartItemId}
                  key={`item-${cartItemId}`}
                />
              ))}
            </>
          ) : (
            <List skin="minimal">
              {cartItemIds.map(cartItemId => (
                <ItemModWrapper itemId={cartItemId} key={`item-${cartItemId}`}>
                  <ListItem key={`item-${cartItemId}`}>
                    <CartItem
                      cartItemId={cartItemId}
                      // individual items in a group should not support these actions
                      supportedActions={{
                        remove: false,
                        updateQuantity: false,
                      }}
                      shouldHandleRemove={false}
                    />
                  </ListItem>
                </ItemModWrapper>
              ))}
            </List>
          )}
          {displayItemGroupPricingSummary && (
            <Row>
              <Column paddingTop="6" span={12}>
                <Divider marginY="4" />
                <ItemGroupPricingSummary groupId={groupId} />
              </Column>
            </Row>
          )}
        </Card>
      </Column>
    </Row>
  );
};

CartItemGroup.propTypes = {
  cartItemIds: PropTypes.array.isRequired,
  groupId: PropTypes.string.isRequired,
};
